import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const LeagueContext = createContext({});

export const useLeague = () => {
  const context = useContext(LeagueContext);
  if (!context) throw new Error('useLeague must be used within LeagueProvider');
  return context;
};

const generateCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
};

export const LeagueProvider = ({ children }) => {
  const { user } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [activeLeague, setActiveLeagueState] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'owner' | 'admin' | 'member'
  const [leagueMembers, setLeagueMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [leaguesError, setLeaguesError] = useState(null);

  const fetchLeagues = useCallback(async () => {
    if (!supabase || !user) {
      setLeagues([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLeaguesError(null);
    try {
      const { data, error } = await supabase
        .from('league_members')
        .select('role, league_id, leagues(*)')
        .eq('user_id', user.id);
      if (error) throw error;

      const leagueList = (data || []).map((m) => ({
        id: m?.leagues?.id,
        name: m?.leagues?.name,
        code: m?.leagues?.code,
        created_at: m?.leagues?.created_at,
        created_by: m?.leagues?.created_by,
        archived_at: m?.leagues?.archived_at ?? null,
        deleted_at: m?.leagues?.deleted_at ?? null,
        role: m?.role,
      })).filter((l) => l.id);

      // Best-effort auto-purge: hard-delete any league the user owns that
      // has been soft-deleted for more than 30 days. Errors are swallowed —
      // this is opportunistic cleanup that runs whenever an owner loads.
      const now = Date.now();
      const PURGE_AFTER_MS = 30 * 24 * 60 * 60 * 1000;
      const toPurge = leagueList.filter(
        (l) => l.role === 'owner' && l.deleted_at && (now - new Date(l.deleted_at).getTime()) > PURGE_AFTER_MS
      );
      if (toPurge.length > 0) {
        await Promise.all(
          toPurge.map((l) => supabase.from('leagues').delete().eq('id', l.id).then(() => {}, () => {}))
        );
        // Drop purged leagues from the in-memory list
        const purgedIds = new Set(toPurge.map((l) => l.id));
        for (let i = leagueList.length - 1; i >= 0; i--) {
          if (purgedIds.has(leagueList[i].id)) leagueList.splice(i, 1);
        }
      }

      setLeagues(leagueList);

      // Restore active league from localStorage (skip archived + deleted)
      const activeLeagues = leagueList.filter((l) => !l.archived_at && !l.deleted_at);
      const savedId = localStorage.getItem('hoopstats_active_league');
      const saved = activeLeagues.find((l) => l.id === savedId);
      if (saved) {
        setActiveLeagueState(saved);
        setUserRole(saved.role);
      } else if (activeLeagues.length === 1) {
        setActiveLeagueState(activeLeagues[0]);
        setUserRole(activeLeagues[0].role);
        localStorage.setItem('hoopstats_active_league', activeLeagues[0].id);
      } else {
        setActiveLeagueState(null);
        setUserRole(null);
      }
    } catch (err) {
      console.error('Failed to fetch leagues:', err);
      setLeaguesError(err?.message || 'Failed to load leagues');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchLeagues();
  }, [fetchLeagues]);

  // Fetch league members with profile names
  const fetchLeagueMembers = useCallback(async () => {
    if (!supabase || !activeLeague) {
      setLeagueMembers([]);
      return;
    }
    setMembersLoading(true);
    try {
      // Fetch members separately from profiles (no direct FK between tables)
      const { data: members, error: membersError } = await supabase
        .from('league_members')
        .select('id, user_id, role, joined_at')
        .eq('league_id', activeLeague.id)
        .order('joined_at', { ascending: true });
      if (membersError) throw membersError;

      // Fetch profiles for these members
      const userIds = (members || []).map((m) => m.user_id);
      const profilesMap = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', userIds);
        (profiles || []).forEach((p) => { profilesMap[p.id] = p.full_name; });
      }

      // For the current user, fall back to auth metadata if profile is missing
      const currentUserName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || null;

      setLeagueMembers((members || []).map((m) => ({
        id: m.id,
        userId: m.user_id,
        role: m.role,
        joinedAt: m.joined_at,
        name: profilesMap[m.user_id] || (m.user_id === user?.id ? currentUserName : null) || 'Member',
      })));
    } catch (err) {
      console.error('Failed to fetch league members:', err);
    } finally {
      setMembersLoading(false);
    }
  }, [activeLeague]);

  useEffect(() => {
    fetchLeagueMembers();
  }, [fetchLeagueMembers]);

  // Realtime subscription: refresh members when league_members changes
  useEffect(() => {
    if (!supabase || !activeLeague) return;

    const channel = supabase
      .channel(`league_members_${activeLeague.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'league_members', filter: `league_id=eq.${activeLeague.id}` },
        () => {
          fetchLeagueMembers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase, activeLeague, fetchLeagueMembers]);

  const switchLeague = useCallback((leagueId) => {
    const league = leagues.find((l) => l.id === leagueId);
    if (league) {
      setActiveLeagueState(league);
      setUserRole(league.role);
      localStorage.setItem('hoopstats_active_league', leagueId);
    }
  }, [leagues]);

  const createLeague = useCallback(async (name) => {
    if (!supabase || !user) throw new Error('Not authenticated');

    const code = generateCode();
    const { data: league, error: leagueError } = await supabase
      .from('leagues')
      .insert({ name, code, created_by: user.id })
      .select()
      .single();
    if (leagueError) throw leagueError;

    const { error: memberError } = await supabase
      .from('league_members')
      .insert({ league_id: league.id, user_id: user.id, role: 'owner' });
    if (memberError) throw memberError;

    await fetchLeagues();
    return league;
  }, [user, fetchLeagues]);

  const joinLeague = useCallback(async (code) => {
    if (!supabase || !user) throw new Error('Not authenticated');

    const { data: league, error: lookupError } = await supabase
      .from('leagues')
      .select('id, name, code')
      .eq('code', code.toUpperCase().trim())
      .single();
    if (lookupError || !league) throw new Error('League not found. Check the code and try again.');

    const existing = leagues.find((l) => l.id === league.id);
    if (existing) throw new Error('You are already a member of this league.');

    const { error: joinError } = await supabase
      .from('league_members')
      .insert({ league_id: league.id, user_id: user.id, role: 'member' });
    if (joinError) throw joinError;

    await fetchLeagues();
    return league;
  }, [user, leagues, fetchLeagues]);

  const leaveLeague = useCallback(async (leagueId) => {
    if (!supabase || !user) return;
    const { error } = await supabase.from('league_members').delete().eq('league_id', leagueId).eq('user_id', user.id);
    if (error) throw error;
    if (activeLeague?.id === leagueId) {
      setActiveLeagueState(null);
      setUserRole(null);
      localStorage.removeItem('hoopstats_active_league');
    }
    await fetchLeagues();
  }, [user, activeLeague, fetchLeagues]);

  const clearActiveLeague = useCallback(() => {
    setActiveLeagueState(null);
    setUserRole(null);
    localStorage.removeItem('hoopstats_active_league');
  }, []);

  const archiveLeague = useCallback(async (leagueId) => {
    if (!supabase) throw new Error('Database not connected');
    const { error } = await supabase
      .from('leagues')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', leagueId);
    if (error) throw error;
    if (activeLeague?.id === leagueId) {
      setActiveLeagueState(null);
      setUserRole(null);
      localStorage.removeItem('hoopstats_active_league');
    }
    await fetchLeagues();
  }, [activeLeague, fetchLeagues]);

  const unarchiveLeague = useCallback(async (leagueId) => {
    if (!supabase) throw new Error('Database not connected');
    const { error } = await supabase
      .from('leagues')
      .update({ archived_at: null })
      .eq('id', leagueId);
    if (error) throw error;
    await fetchLeagues();
  }, [fetchLeagues]);

  // Soft-delete: marks the league as deleted with a 30-day grace period.
  // Hard-delete happens automatically on next load after 30 days, or via purgeLeague.
  const deleteLeague = useCallback(async (leagueId) => {
    if (!supabase) throw new Error('Database not connected');
    const { error } = await supabase
      .from('leagues')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', leagueId);
    if (error) throw error;
    if (activeLeague?.id === leagueId) {
      setActiveLeagueState(null);
      setUserRole(null);
      localStorage.removeItem('hoopstats_active_league');
    }
    await fetchLeagues();
  }, [activeLeague, fetchLeagues]);

  const restoreLeague = useCallback(async (leagueId) => {
    if (!supabase) throw new Error('Database not connected');
    const { error } = await supabase
      .from('leagues')
      .update({ deleted_at: null })
      .eq('id', leagueId);
    if (error) throw error;
    await fetchLeagues();
  }, [fetchLeagues]);

  // Immediate hard-delete (skips the grace period). Use sparingly.
  const purgeLeague = useCallback(async (leagueId) => {
    if (!supabase) throw new Error('Database not connected');
    const { error } = await supabase.from('leagues').delete().eq('id', leagueId);
    if (error) throw error;
    if (activeLeague?.id === leagueId) {
      setActiveLeagueState(null);
      setUserRole(null);
      localStorage.removeItem('hoopstats_active_league');
    }
    await fetchLeagues();
  }, [activeLeague, fetchLeagues]);

  // Owner can promote member to admin or demote admin to member
  const updateMemberRole = useCallback(async (memberId, newRole) => {
    if (!supabase || userRole !== 'owner') throw new Error('Only the league owner can change roles');
    if (newRole !== 'admin' && newRole !== 'member') throw new Error('Invalid role');

    const { error } = await supabase
      .from('league_members')
      .update({ role: newRole })
      .eq('id', memberId);
    if (error) throw error;

    await fetchLeagueMembers();
  }, [userRole, fetchLeagueMembers]);

  // Owner or admin can remove a member (but not owner)
  const removeMember = useCallback(async (memberId) => {
    if (!supabase) return;
    if (userRole !== 'owner' && userRole !== 'admin') throw new Error('Not authorized');

    const member = leagueMembers.find((m) => m.id === memberId);
    if (member?.role === 'owner') throw new Error('Cannot remove the league owner');
    if (userRole === 'admin' && member?.role === 'admin') throw new Error('Admins cannot remove other admins');

    const { error } = await supabase
      .from('league_members')
      .delete()
      .eq('id', memberId);
    if (error) throw error;

    await fetchLeagueMembers();
  }, [userRole, leagueMembers, fetchLeagueMembers]);

  return (
    <LeagueContext.Provider value={{
      leagues,
      activeLeague,
      userRole,
      loading,
      leaguesError,
      isAdmin: userRole === 'admin' || userRole === 'owner',
      isOwner: userRole === 'owner',
      leagueMembers,
      membersLoading,
      createLeague,
      joinLeague,
      switchLeague,
      leaveLeague,
      archiveLeague,
      unarchiveLeague,
      deleteLeague,
      restoreLeague,
      purgeLeague,
      clearActiveLeague,
      fetchLeagues,
      fetchLeagueMembers,
      updateMemberRole,
      removeMember,
    }}>
      {children}
    </LeagueContext.Provider>
  );
};

export default LeagueContext;
